import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import api from "@/lib/api";
import { ArrowLeft, FileText } from "@phosphor-icons/react";

function HeaderBar() {
  return (
    <header className="border-b border-border">
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="brand-link">
          <img src="/easy-mail-logo.png" alt="Easy Mail" className="h-7 w-auto" />
          <span className="font-display text-xl font-bold">EASY MAIL</span>
        </Link>
        <Link to="/" className="text-sm hover:text-primary">Home</Link>
      </div>
    </header>
  );
}

function FooterBar() {
  return (
    <div data-testid="footer-legal" className="border-t border-border py-6 text-center text-xs text-muted-foreground">
      <Link to="/legal" className="underline mx-2" data-testid="footer-link-legal">Legal Documents and Policies</Link>
    </div>
  );
}

function LegalIndex() {
  const [docs, setDocs] = useState(null);
  useEffect(() => {
    api.get("/legal/docs").then((r) => setDocs(r.data.items)).catch(() => setDocs([]));
  }, []);

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <HeaderBar />
      <main className="flex-1 max-w-4xl mx-auto px-6 py-20 w-full">
        <div className="section-tag mb-4">/ Legal Documents and Policies</div>
        <h1 className="font-display text-5xl sm:text-6xl mb-4">Everything in one place.</h1>
        <p className="text-lg text-muted-foreground mb-12">
          The contracts, policies, and disclosures that govern Easy Mail. Each is also the legal source of truth for the corresponding feature in the app.
        </p>
        {docs === null && <div className="font-mono text-sm">Loading…</div>}
        {docs && docs.length === 0 && <div className="font-mono text-sm">No documents available.</div>}
        {docs && docs.length > 0 && (
          <div className="space-y-px bg-border" data-testid="legal-doc-list">
            {docs.map((d) => (
              <Link
                key={d.slug}
                to={`/legal/${d.slug}`}
                className="block bg-background p-6 hover:bg-secondary/40 transition-colors"
                data-testid={`legal-doc-${d.slug}`}
              >
                <div className="flex items-start gap-4">
                  <FileText size={26} weight="duotone" className="text-primary shrink-0 mt-1" />
                  <div className="flex-1">
                    <div className="font-display text-xl font-bold mb-1">{d.title}</div>
                    <p className="text-sm text-muted-foreground">{d.summary}</p>
                  </div>
                  <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground self-center">Read →</div>
                </div>
              </Link>
            ))}
          </div>
        )}
        <div className="border border-foreground p-6 mt-12">
          <div className="section-tag mb-2">Need help?</div>
          <p className="text-sm">E-Mail <span className="font-mono">easymailsupport@gmail.com</span></p>
        </div>
      </main>
      <FooterBar />
    </div>
  );
}

function LegalDoc() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [doc, setDoc] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    setDoc(null); setError(null);
    api.get(`/legal/docs/${slug}`)
      .then((r) => setDoc(r.data))
      .catch((e) => setError(e?.response?.status === 404 ? "Document not found" : "Failed to load"));
  }, [slug]);

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <HeaderBar />
      <main className="flex-1 max-w-3xl mx-auto px-6 py-12 w-full">
        <button
          onClick={() => nav("/legal")}
          className="inline-flex items-center gap-2 text-sm font-mono uppercase tracking-widest hover:text-primary mb-8"
          data-testid="back-to-legal"
        >
          <ArrowLeft size={14} /> All legal documents
        </button>
        {error && (
          <div className="border border-destructive p-6 text-sm">
            <div className="section-tag mb-2 text-destructive">Error</div>
            {error}
          </div>
        )}
        {!doc && !error && <div className="font-mono text-sm">Loading…</div>}
        {doc && (
          <article className="prose prose-sm sm:prose-base max-w-none" data-testid={`legal-content-${doc.slug}`}>
            <div className="section-tag mb-4">/ {doc.title}</div>
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                h1: ({ node, ...p }) => <h1 className="font-display text-4xl sm:text-5xl mb-4 leading-tight" {...p} />,
                h2: ({ node, ...p }) => <h2 className="font-display text-2xl font-bold mt-8 mb-2 border-b border-border pb-1" {...p} />,
                h3: ({ node, ...p }) => <h3 className="font-display text-xl font-bold mt-6 mb-2" {...p} />,
                p:  ({ node, ...p }) => <p className="text-foreground/85 leading-relaxed mb-4" {...p} />,
                ul: ({ node, ...p }) => <ul className="list-disc pl-6 mb-4 space-y-1 text-foreground/85" {...p} />,
                ol: ({ node, ...p }) => <ol className="list-decimal pl-6 mb-4 space-y-1 text-foreground/85" {...p} />,
                li: ({ node, ...p }) => <li className="leading-relaxed" {...p} />,
                code: ({ node, ...p }) => <code className="font-mono text-xs bg-secondary px-1.5 py-0.5" {...p} />,
                a:  ({ node, ...p }) => <a className="underline hover:text-primary" {...p} />,
                hr: () => <hr className="my-8 border-border" />,
                table: ({ node, ...p }) => <table className="border-collapse border border-border my-4 w-full text-sm" {...p} />,
                th: ({ node, ...p }) => <th className="border border-border bg-secondary/40 px-3 py-2 text-left font-mono uppercase text-xs tracking-widest" {...p} />,
                td: ({ node, ...p }) => <td className="border border-border px-3 py-2" {...p} />,
              }}
            >
              {doc.content}
            </ReactMarkdown>
          </article>
        )}
      </main>
      <FooterBar />
    </div>
  );
}

export default function Legal() {
  const { slug } = useParams();
  return slug ? <LegalDoc /> : <LegalIndex />;
}
